/**
 * Browser-side push subscription.
 *
 * Push is unusually easy to break silently: the permission can be denied at the
 * OS level rather than the browser's, iOS delivers only to an installed PWA,
 * and a service worker that failed to register produces no error anywhere the
 * user can see. So this module's job is less "subscribe" and more "work out
 * exactly why it cannot, and say so".
 */

import { api } from './api';
import { isIos } from './platform';

export type PushState =
  | 'unsupported'
  | 'needs-install'
  | 'denied'
  | 'unavailable'
  | 'prompt'
  | 'subscribed';

export interface PushStatus {
  state: PushState;
  /** What to tell the user. Empty when subscribed. */
  reason: string;
}

interface PushConfig {
  available: boolean;
  publicKey: string | null;
}

/** True when running as an installed PWA rather than in a browser tab. */
export function isInstalled(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // Safari's own, non-standard, and the only reliable signal on iOS.
    ('standalone' in window.navigator && (window.navigator as { standalone?: boolean }).standalone === true)
  );
}

/**
 * Works out where this browser stands.
 *
 * The iOS case is checked before support, because on iOS Safari the push APIs
 * are genuinely absent until the app is installed — so the honest answer is
 * "add it to your home screen", not "your browser does not support this".
 */
export async function getStatus(): Promise<PushStatus> {
  if (isIos() && !isInstalled()) {
    return {
      state: 'needs-install',
      reason:
        'On iPhone and iPad, notifications only work once QuickStock is added to the home screen.',
    };
  }

  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return {
      state: 'unsupported',
      reason: 'This browser cannot receive push notifications.',
    };
  }

  if (Notification.permission === 'denied') {
    return {
      state: 'denied',
      // The browser will not ask again once refused, and no amount of clicking
      // in the app can change that — it has to be reset in site settings.
      reason:
        'Notifications are blocked for this site. Allow them in your browser’s site settings, then come back.',
    };
  }

  const config = await api.get<PushConfig>('/push/config');
  if (!config.available || !config.publicKey) {
    return {
      state: 'unavailable',
      reason: 'Push notifications are not set up on this QuickStock server.',
    };
  }

  const registration = await navigator.serviceWorker.getRegistration();
  const existing = await registration?.pushManager.getSubscription();

  return existing
    ? { state: 'subscribed', reason: '' }
    : { state: 'prompt', reason: '' };
}

/**
 * Asks permission and registers a subscription.
 *
 * Throws with a readable message rather than a DOMException, because whatever
 * comes back here goes straight onto the screen.
 */
export async function subscribe(): Promise<void> {
  const config = await api.get<PushConfig>('/push/config');
  if (!config.available || !config.publicKey) {
    throw new Error('Push notifications are not set up on this QuickStock server.');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Notifications were not allowed.');
  }

  const registration = await navigator.serviceWorker.ready;

  // An existing subscription is reused rather than replaced: re-subscribing
  // with the same key returns the same endpoint anyway, and unsubscribing first
  // would create a window where a notification has nowhere to go.
  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      // Required to be true by every browser: a push must always be shown.
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(config.publicKey),
    }));

  const json = subscription.toJSON();
  if (!json.keys?.p256dh || !json.keys.auth) {
    throw new Error('The browser produced a subscription without encryption keys.');
  }

  await api.post('/push/subscriptions', {
    endpoint: subscription.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
  });
}

export async function unsubscribe(): Promise<void> {
  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) return;

  // Told the server first. If the browser-side unsubscribe succeeded and the
  // request then failed, the server would keep pushing to an endpoint that no
  // longer exists — invisible to the user and only cleared when the push
  // service eventually returns 410.
  await api.delete('/push/subscriptions', { endpoint: subscription.endpoint });
  await subscription.unsubscribe();
}

export async function sendTest(): Promise<{ sent: number; failed: number }> {
  return api.post<{ sent: number; failed: number }>('/push/test');
}

/**
 * VAPID keys travel as base64url; `applicationServerKey` wants raw bytes.
 *
 * Returns an ArrayBuffer rather than a Uint8Array. Both satisfy `BufferSource`
 * at runtime, but recent lib.dom types narrow the view form to
 * `ArrayBufferView<ArrayBuffer>` and a plain `Uint8Array` is typed over
 * `ArrayBufferLike`, which includes `SharedArrayBuffer`. The buffer sidesteps
 * the mismatch without a cast.
 */
function urlBase64ToUint8Array(base64: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalised = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(normalised);
  const output = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output.buffer;
}
